import { cn } from '@/lib/utils';

/**
 * SAB-959 — ALL / AVAILABLE, replacing the full-width HIDE DRAFTED card on mobile.
 *
 * ── SAME STATE, SAME KEY ────────────────────────────────────────────────────
 *
 * This is a different SHAPE for one boolean, not a new setting. `AVAILABLE` is
 * `hideDrafted === true`, which is the existing default, and the value still round-trips
 * through `loadHideDrafted`/`saveHideDrafted` under `sf_nfl_draft_hide_drafted:<leagueId>`.
 * A user who had the old toggle on opens the new control on AVAILABLE; nobody is reset.
 *
 * ── WHY A SEGMENT AND NOT A SWITCH ──────────────────────────────────────────
 *
 * HideDraftedControl had to solve "lit means hidden", which reads backwards to about half
 * of people the first time. Naming both ends removes the question rather than answering it:
 * ALL and AVAILABLE each say what the list will contain, so neither depends on knowing
 * which way the control points.
 *
 * ── WHAT IS LOST, DELIBERATELY ──────────────────────────────────────────────
 *
 * The old control showed a live count of what the hide was withholding ("HIDE DRAFTED 3"),
 * which is how a user could tell the filter was doing something. The design has no room for
 * it, so `hiddenCount` is accepted and used only for the accessible description -- the
 * number survives for screen readers even though the visual is gone.
 */

export interface AvailabilitySegmentProps {
  /** `true` = AVAILABLE (drafted hidden). The existing hideDrafted boolean, unchanged. */
  hideDrafted: boolean;
  onChange: (hidden: boolean) => void;
  /** Rows the hide is currently withholding. 0 while searching. */
  hiddenCount?: number;
}

const SEG = [
  { id: 'ALL', hidden: false },
  { id: 'AVAILABLE', hidden: true },
] as const;

export default function AvailabilitySegment({
  hideDrafted, onChange, hiddenCount = 0,
}: AvailabilitySegmentProps) {
  return (
    <div
      data-testid="pool-availability"
      data-value={hideDrafted ? 'AVAILABLE' : 'ALL'}
      role="group"
      aria-label="Which players to show"
      className="flex flex-none items-center gap-[2px] rounded-full bg-secondary p-[3px]"
    >
      {SEG.map(({ id, hidden }) => {
        const selected = hideDrafted === hidden;
        return (
          <button
            key={id}
            data-testid={`pool-availability-${id}`}
            data-selected={selected || undefined}
            aria-pressed={selected}
            aria-label={
              hidden
                ? `Available only${hiddenCount > 0 ? `, hiding ${hiddenCount} drafted` : ''}`
                : 'All players, including drafted'
            }
            // src/index.css:411 floors every button at 44x44; the design's segment is 30px
            // tall. It opts out and buys the tap target back with the overlay below -- 30px
            // plus 7px above and below is 44.
            data-compact=""
            onClick={() => onChange(hidden)}
            className={cn(
              'relative h-[30px] rounded-full border-0 px-[11px]',
              'font-display text-[10.5px] font-bold tracking-[0.08em] transition-colors',
              'after:absolute after:inset-x-0 after:inset-y-[-7px] after:content-[""]',
              // The design's selected text is #fff. NOT `text-primary-foreground`, which in
              // this theme is 222 47% 6% -- near-black, and unreadable on the orange fill.
              selected ? 'bg-primary text-white' : 'bg-transparent text-muted-foreground',
            )}
          >
            {id}
          </button>
        );
      })}
    </div>
  );
}
